function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import { jsx } from 'panel';
import type { VNode } from 'snabbdom';
import { IconSizeType, MPProperties } from 'common/constants';
import { TooltipEl } from 'common/components/tooltip';
import type { TableRow } from 'common/components/table/types';
import { getProfileUrl } from 'common/util/format-property';
import { TrackedUserIconEl } from 'common/widgets/tracked-user-icon';
import { renameEvent } from 'common/report/util/events';
import { formatEventProperty } from 'common/report/events/utils';
import { ProfileIconClickEvent, ViewInInsightsClickEvent, ViewInLexiconClickEvent, ViewReplayClickEvent } from 'common/report/events/types';
import { SelectEl } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { ChangeEvent } from 'common/components/select/types';
import { SectionItem } from 'common/components/items-menu/types';
import { LocalizationStore } from 'common/i18n/store';
import { CurrentProject } from '../model-resources/projects';
import { isSessionReplayEventFeedEnabled } from '../util/feature-sampling';
import { TrackEvents } from './tracking';
export function renderCellProperty(row: TableRow, property: string, i18n: LocalizationStore): VNode {
  const {
    formattedValue: propertyValue
  } = formatEventProperty(property, row.properties[property], {
    relativeTime: true,
    i18n
  });
  let tooltip = propertyValue;
  if (property === MPProperties.Time || property === MPProperties.$Time) {
    const {
      formattedValue
    } = formatEventProperty(property, row.properties[property], {
      relativeTime: false,
      i18n
    });
    tooltip = formattedValue;
  }
  return <div sel='.cell-col' data-sentry-component="renderCellProperty" data-sentry-source-file="table-cell-mixins.tsx">
      {propertyValue}
      <TooltipEl data-sentry-element="TooltipEl" data-sentry-source-file="table-cell-mixins.tsx">{tooltip}</TooltipEl>
    </div>;
}
export function renderCell(row: TableRow, name: string, i18n: LocalizationStore, dataGroupId?: string): VNode {
  switch (name) {
    case "$event_name":
      return renderEventName(row);
    case "$distinct_id":
      return renderDistinctId(row, dataGroupId);
    case "$time":
      return renderCellProperty(row, "time", i18n);
    default:
      return renderCellProperty(row, name, i18n);
  }
}
export function trackClick(ev: MouseEvent, profileUrl: string, propName: string) {
  // prevent row from being expanded when distinct id is clicked
  ev.stopPropagation();
  if (propName === "Distinct ID") {
    mixpanel.track(TrackEvents.ProfileClicked, {
      clicked: "distinct-id",
      url: profileUrl
    });
  }
}
function renderDistinctId(row: TableRow, dataGroupId?: string): VNode {
  const distinctId = row.properties.distinct_id;
  // TODO see if we can get this dataGroupId stuff into format-property
  const profileUrl = getProfileUrl(distinctId, dataGroupId);
  return <div sel='.cell-distinct-id' data-sentry-component="renderDistinctId" data-sentry-source-file="table-cell-mixins.tsx">
      {distinctId ? <>
          <a attrs={{
        href: profileUrl,
        target: "_blank",
        rel: "noopener noreferrer"
      }} on={{
        click: ev => trackClick(ev, profileUrl, "Distinct ID")
      }}>
            {distinctId}
          </a>
          {distinctId ? <TooltipEl>{distinctId}</TooltipEl> : null}
        </> : "-"}
    </div>;
}
function renderEventName(row: TableRow): VNode {
  const eventName = renameEvent(row.event);
  return <div sel='.cell-event' data-sentry-component="renderEventName" data-sentry-source-file="table-cell-mixins.tsx">
      <div sel='.cell-event-name'>{eventName}</div>
      <TooltipEl data-sentry-element="TooltipEl" data-sentry-source-file="table-cell-mixins.tsx">{eventName}</TooltipEl>
    </div>;
}
export function renderProfileIcon(row: TableRow, rowIdx: number, selectedDistinctId: string, selectedRowIdx: number): VNode {
  const distinctId = row.properties.distinct_id;
  const email = row.properties.Email;
  const handleProfileIconClick = (ev: Event, opts: {
    rowIdx: number;
    distinctId: string;
  }) => {
    // prevent row from being expanded when profile icon is clicked
    ev.stopPropagation();
    if (distinctId) {
      const profileIconClickEvent: ProfileIconClickEvent = new CustomEvent("profileIconClick", {
        composed: true,
        bubbles: true,
        detail: {
          distinctId: opts.distinctId,
          rowIdx: opts.rowIdx
        }
      });
      ev.target.dispatchEvent(profileIconClickEvent);
    }
  };
  return <div sel='.cell-profile-icon' attrs={{
    title: distinctId ? "View Profile" : "No Distinct Id On Event"
  }} class={{
    'cell-profile-icon-clickable': !!distinctId,
    'cell-profile-icon-selected': !!distinctId && selectedDistinctId === distinctId,
    'cell-profile-icon-active': !!distinctId && selectedRowIdx === rowIdx
  }} on={{
    click: ev => handleProfileIconClick(ev, {
      rowIdx,
      distinctId
    })
  }} data-sentry-component="renderProfileIcon" data-sentry-source-file="table-cell-mixins.tsx">
      <TrackedUserIconEl attrs={{
      'distinct-id': distinctId,
      email,
      size: IconSizeType.Small
    }} data-sentry-element="TrackedUserIconEl" data-sentry-source-file="table-cell-mixins.tsx" />
    </div>;
}
const handleOverflowChange = (ev: ChangeEvent, row: TableRow) => {
  const {
    value
  } = ev.detail.item;
  switch (value) {
    case "viewInInsights":
      {
        const viewInInsightsClickEvent: ViewInInsightsClickEvent = new CustomEvent("viewInInsightsClick", {
          composed: true,
          bubbles: true,
          detail: {
            eventName: row.event
          }
        });
        ev.target.dispatchEvent(viewInInsightsClickEvent);
        break;
      }
    case "viewInLexicon":
      {
        const viewInLexiconClickEvent: ViewInLexiconClickEvent = new CustomEvent("viewInLexiconClick", {
          composed: true,
          bubbles: true,
          detail: {
            eventName: row.event
          }
        });
        ev.target.dispatchEvent(viewInLexiconClickEvent);
        break;
      }
    case "viewReplay":
      {
        var _row$properties;
        const viewReplayClickEvent: ViewReplayClickEvent = new CustomEvent("viewReplayClick", {
          composed: true,
          bubbles: true,
          detail: {
            eventName: row.event,
            insertId: row === null || row === void 0 || (_row$properties = row.properties) === null || _row$properties === void 0 ? void 0 : _row$properties.$insert_id
          }
        });
        ev.target.dispatchEvent(viewReplayClickEvent);
        break;
      }
    default:
      throw Error("Unexpected value for overflow menu selection: ".concat(value));
  }
};
export function renderActionsMenu(row: TableRow, i18n: LocalizationStore): VNode {
  let currentProject: CurrentProject = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  const viewInInsights: SectionItem = {
    label: _ref => {
      let {
        t
      } = _ref;
      return t("events.view-in-insights");
    },
    value: "viewInInsights"
  };
  const viewInLexicon: SectionItem = {
    label: _ref2 => {
      let {
        t
      } = _ref2;
      return t("events.view-in-lexicon");
    },
    value: "viewInLexicon"
  };
  const disabledMessage = i18n.t("session-replay.integrate-replay", {
    docsLink: "https://docs.mixpanel.com/docs/session-replay"
  });
  const disabledAttrs: Partial<SectionItem> = !(currentProject !== null && currentProject !== void 0 && currentProject.hasIntegratedSessionReplay) ? {
    isDisabled: true,
    tooltip: {
      text: disabledMessage
    }
  } : {};
  const viewReplay: SectionItem = _objectSpread({
    label: _ref3 => {
      let {
        t
      } = _ref3;
      return t("events.view-replay");
    },
    value: "viewReplay"
  }, disabledAttrs);
  const items = isSessionReplayEventFeedEnabled(currentProject) ? [viewInInsights, viewInLexicon, viewReplay] : [viewInInsights, viewInLexicon];
  return <SelectEl attrs={{
    icon: {
      leftIcon: "hor-ellipsis"
    },
    'icon-only': true,
    open: false,
    theme: SelectTheme.Tertiary,
    'disable-search': true,
    'menu-width': 160,
    elref: "overflowMenu"
  }} on={{
    click: ev => {
      // prevent row from being expanded when clicked
      ev.stopPropagation();
    },
    change: ev => handleOverflowChange(ev, row)
  }} props={{
    sectionsFn: () => [{
      items
    }]
  }} data-sentry-element="SelectEl" data-sentry-component="renderActionsMenu" data-sentry-source-file="table-cell-mixins.tsx" />;
}